import type {
  AbilityId,
  AuraId,
  ClassId,
  Phase,
  ResourceType,
} from '@arena/shared';
import type { LossOfControl } from './lossOfControl';
import type { MeterRow } from './meter';

export interface AuraView {
  auraId: AuraId;
  name: string;
  icon: string;
  description: string;
  harmful: boolean;
  dispel: 'magic' | null;
  /** Damage left to absorb; 0 for none. */
  absorb: number;
  /** One of the player's own auras they may click off. */
  cancelable: boolean;
  remaining: number;
  duration: number;
}

export interface Pool {
  type: ResourceType;
  value: number;
  max: number;
}

export interface FrameView {
  id: string;
  name: string;
  /** The class it's playing. */
  classId: ClassId;
  /** Its other class; null for hub units, which show only the one they're drawn as. */
  other: ClassId | null;
  friendly: boolean;
  hp: number;
  maxHp: number;
  /** Each class's resource, the playing one's first. */
  pools: Pool[];
  dead: boolean;
  /** Released, walking as a spirit. */
  ghost: boolean;
  stealthed: boolean;
  /** Not in the latest snapshot: stealthed enemies keep their arena frame. */
  hidden: boolean;
  cast: {
    name: string;
    progress: number;
    remaining: number;
    channel: boolean;
  } | null;
  auras: AuraView[];
  distance: number | null;
}

export interface ActionView {
  abilityId: AbilityId;
  name: string;
  icon: string;
  key: string;
  cooldown: number;
  cooldownDuration: number;
  noResource: boolean;
  /** Uses left this match, for abilities with a limit. */
  uses: number | null;
  outOfRange: boolean;
  /** Refused by the caster's or target's state: needs stealth, combo points, a low target, or its school is locked out. */
  unusable: boolean;
  queued: boolean;
  /** A cancelable ability whose aura is up: pressing it cancels the aura. */
  active: boolean;
}

/** An aura's time left as shown on its icon. */
export const auraSeconds = (s: number): string =>
  s >= 60
    ? `${Math.ceil(s / 60)}m`
    : s >= 10
      ? `${Math.ceil(s)}`
      : s.toFixed(1);

/** What the game draws in Svelte. The engine overwrites it at a modest rate. */
export const hud = $state({
  ready: false,
  /** Files `Game.start` is waiting on, until `ready`. */
  loading: { done: 0, total: 0 },
  /** Frames drawn over the last second. */
  fps: 0,
  phase: 'prep' as Phase,
  phaseRemaining: 0,
  dampening: 0,
  player: null as FrameView | null,
  target: null as FrameView | null,
  targetTarget: null as FrameView | null,
  team: [] as FrameView[],
  enemies: [] as FrameView[],
  /** The playing class's spells, then the utility slots. */
  actions: [] as ActionView[],
  /** The other class's spells, shown above for their cooldowns. */
  offActions: [] as ActionView[],
  swap: null as ActionView | null,
  combo: 0,
  showCombo: false,
  lossOfControl: null as LossOfControl | null,
  /** The player's next auto-attack, while auto-attack is on. */
  swing: null as { progress: number; remaining: number } | null,
  meter: { damage: [] as MeterRow[], healing: [] as MeterRow[], seconds: 1 },
  error: null as { text: string; at: number } | null,
  /** The hub NPC whose dialog is open: right-clicked in range, closed on walking away. */
  npc: null as string | null,
  /** Toggled from the UI, and opened by an Escape with nothing else to do. */
  settingsOpen: false,
  /** The bottom-left button panel that's open; Settings is `settingsOpen`. */
  panel: null as Panel | null,
});

export type Panel =
  | 'spellbook'
  | 'talents'
  | 'classes'
  | 'leaderboard'
  | 'achievements'
  | 'character';

/** Opens `panel`, or closes it if it's the one open; either way Settings closes. */
export function togglePanel(panel: Panel): void {
  hud.panel = hud.panel === panel ? null : panel;
  hud.settingsOpen = false;
}
